import { Injectable, inject } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { CDNCachingConfig } from '@frontend/sports/common/client-config-data-access';
import { ISchemaMarkupResolver } from '@frontend/sports/schema-markup/feature';
import { WidgetLayoutResponse, WidgetType } from '@frontend/sports/types/components/widget';
import { ComposableWidgetPayload } from '@frontend/sports/types/components/widget/types';
import { uniqBy } from 'lodash-es';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class WidgetSchemaMarkupResolver implements ISchemaMarkupResolver<WidgetLayoutResponse> {
    private cdnCachingConfig = inject(CDNCachingConfig);

    private readonly widgetsWithFixtures = [WidgetType.NextToGo, WidgetType.TabbedGrid, WidgetType.OutrightsGrid];

    private readonly widgetsWithFixturesOrComposable = [...this.widgetsWithFixtures, WidgetType.Composable];

    constructor() {
        this.widgetsWithFixturesOrComposable = this.widgetsWithFixturesOrComposable.filter((items) => !this.cdnCachingConfig.widgets.includes(items));
        this.widgetsWithFixtures = this.widgetsWithFixtures.filter((items) => !this.cdnCachingConfig.widgets.includes(items));
    }

    resolve(model: WidgetLayoutResponse): Observable<Fixture[]> {
        if (!model?.widgets?.length) {
            return of([]);
        }

        const widgetsToConsider = model.widgets
            .filter((w) => this.widgetsWithFixturesOrComposable.includes(w?.type))
            .flatMap((widget) =>
                widget.type === WidgetType.Composable
                    ? (widget.payload as ComposableWidgetPayload)?.items?.flatMap(
                          (slot) => slot?.activeChildren?.filter((w) => this.widgetsWithFixtures.includes(w?.type)) ?? [],
                      )
                    : [widget],
            );

        const uniqueFixtures = uniqBy(
            widgetsToConsider.flatMap((w) => (w.payload as { fixtures: Fixture[] }).fixtures),
            (f) => f.id,
        );

        return of(uniqueFixtures);
    }
}
